import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { ArrowUpRight, Layers, Hammer, PaintBucket, Mountain, Quote, Mail, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Showcase } from "../components/Showcase";
import { SERVICES, CONTACT } from "../lib/api";

// Local photos extracted from TDstaircase portfolio PDF
const HERO_IMG = "/stairs/stair-32.jpg";          // dramatic upward floating staircase
const CRAFT_IMG = "/stairs/stair-11.jpg";         // cantilevered closeup
const PORT_1 = "/stairs/curved-oak.jpg";          // new curved oak (real project)
const PORT_2 = "/stairs/stair-17.jpg";            // open-tread floating
const PORT_3 = "/stairs/stair-19.jpg";            // abstract overhead
const PORT_4 = "/stairs/stair-03.jpg";            // curved continuation
const TEXTURE = "/stairs/stair-26.jpg";           // wood grain detail

const SERVICE_ICONS = {
  floating_stairs: Mountain,
  replacement: Hammer,
  flooring: Layers,
  staining: PaintBucket,
};

const TESTIMONIALS = [
  {
    quote:
      "Tai and his team replaced our old staircase with a stunning floating design. The craftsmanship is on another level.",
    name: "M. Carter",
    role: "Floating staircase · 2024",
  },
  {
    quote:
      "They handled our hardwood flooring and staining in the same visit. Clean work, on schedule, fair price.",
    name: "J. Singh",
    role: "Flooring + staining",
  },
  {
    quote:
      "I called for a quote, got a callback the same day. Quality you can see, and you can feel underfoot.",
    name: "L. Tremblay",
    role: "Staircase replacement",
  },
];

const Eyebrow = ({ children }) => (
  <span className="label-eyebrow">— {children}</span>
);

const Landing = () => {
  return (
    <div className="bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Floating staircase"
            className="w-full h-full object-cover opacity-90"
            style={{ filter: "brightness(1.08) contrast(1.05) saturate(1.04)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/85 via-background/55 to-background/15" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/20" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-28 pb-40 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-up">
            <Eyebrow>TDstaircase</Eyebrow>
            <h1 className="font-display mt-8 text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              We Build
              <br />
              <span className="gold-gradient-text not-italic font-semibold">Quality Stairs.</span>
            </h1>
            <p className="mt-8 text-xl text-muted-foreground max-w-xl leading-relaxed fade-up fade-up-d1">
              Floating staircases, replacements and installations, hardwood flooring,
              and custom staining — built and finished by hand.
            </p>
            <div className="mt-12 flex flex-wrap gap-4 fade-up fade-up-d2">
              <Link to="/book">
                <Button
                  size="lg"
                  className="rounded-none bg-primary text-primary-foreground hover:bg-accent px-10 py-7 text-sm uppercase tracking-[0.22em] shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_40px_rgba(212,175,55,0.45)] transition-all"
                  data-testid="hero-book-btn"
                >
                  Book a quote
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#services">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-none border-primary/60 text-primary hover:bg-primary/10 px-10 py-7 text-sm uppercase tracking-[0.22em]"
                  data-testid="hero-explore-btn"
                >
                  Our services
                </Button>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up fade-up-d3">
            <div className="border hairline border-l-2 border-l-primary/60 bg-background/60 backdrop-blur-md p-10">
              <p className="label-eyebrow">Direct line</p>
              <div className="mt-7 space-y-6">
                <a
                  href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                  className="flex items-center gap-5 group"
                  data-testid="hero-phone"
                >
                  <Phone className="h-7 w-7 text-primary" strokeWidth={1.4} />
                  <span className="font-display text-4xl md:text-5xl gold-gradient-text group-hover:opacity-80 transition-opacity">
                    {CONTACT.phone}
                  </span>
                </a>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="flex items-center gap-5 group"
                  data-testid="hero-email"
                >
                  <Mail className="h-6 w-6 text-primary" strokeWidth={1.4} />
                  <span className="text-2xl text-foreground/90 group-hover:text-primary transition-colors">
                    {CONTACT.email}
                  </span>
                </a>
              </div>
              <div className="gold-divider my-9" />
              <p className="text-lg text-muted-foreground leading-relaxed">
                Every project is measured, milled and finished by the same hands that
                arrive on installation day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
            <div className="lg:col-span-7">
              <Eyebrow>What we do</Eyebrow>
              <h2 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                Four disciplines.
                <br />
                <span className="gold-gradient-text">One uncompromising standard.</span>
              </h2>
            </div>
            <p className="lg:col-span-5 text-muted-foreground text-base leading-relaxed">
              Floating staircases, replacements and new installations, hardwood flooring
              and custom staining — done in-house, start to finish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.map((s, idx) => {
              const Icon = SERVICE_ICONS[s.key] || Hammer;
              // 2x2 bento on md+: 7/5/5/7
              const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];
              const span = spans[idx] || "md:col-span-6";
              return (
                <Link
                  key={s.key}
                  to={`/book?service=${s.key}`}
                  data-testid={`service-card-${s.key}`}
                  className={`${span} group border hairline bg-card hover:border-primary/60 transition-colors duration-500 p-8 lg:p-10 flex flex-col justify-between min-h-[240px] relative overflow-hidden`}
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="max-w-md">
                      <Icon className="h-7 w-7 text-primary mb-6" strokeWidth={1.2} />
                      <h3 className="font-display text-3xl lg:text-4xl leading-tight">
                        {s.label}
                      </h3>
                      <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
                        {s.description}
                      </p>
                    </div>
                    <ArrowUpRight className="h-6 w-6 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                  </div>
                  <div className="mt-8 flex items-center justify-between text-sm uppercase tracking-[0.24em] text-muted-foreground">
                    <span>{s.duration}</span>
                    <span className="text-primary">Book →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CRAFTSMANSHIP */}
      <section id="craftsmanship" className="relative py-32 border-y hairline" data-testid="craft-section">
        <div className="absolute inset-0 opacity-20">
          <img src={TEXTURE} alt="" className="w-full h-full object-cover" style={{ filter: "brightness(1.08) contrast(1.04)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={CRAFT_IMG} alt="TDstaircase craftsmanship" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block border hairline border-l-2 border-l-primary bg-background p-6 max-w-xs">
              <p className="font-display text-2xl">"Patience is the joinery."</p>
              <p className="text-sm uppercase tracking-[0.24em] text-muted-foreground mt-3">— Tai Dang</p>
            </div>
          </div>
          <div className="lg:col-span-6 lg:pl-12">
            <Eyebrow>The workshop</Eyebrow>
            <h2 className="font-display mt-6 text-4xl sm:text-5xl leading-[1.05]">
              A practice rooted in <span className="gold-gradient-text">patience.</span>
            </h2>
            <p className="text-muted-foreground mt-8 leading-relaxed text-base">
              Every project starts with a conversation — never a brochure. We listen to
              the space, the daily flow, the way light moves through your home — then we
              build to match.
            </p>
            <p className="text-muted-foreground mt-5 leading-relaxed text-base">
              From floating treads to full hardwood flooring and custom staining, every
              surface is measured, prepared and signed off by the same hands that install
              it.
            </p>
            <div className="gold-divider my-10 w-40" />
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="label-eyebrow">Materials</p>
                <p className="mt-3 text-sm text-muted-foreground">Oak · Walnut · Maple · Steel</p>
              </div>
              <div>
                <p className="label-eyebrow">Finishes</p>
                <p className="mt-3 text-sm text-muted-foreground">Hand-stained · Hard-oil · Satin · Matte</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="portfolio" className="relative py-32" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <Eyebrow>Our work</Eyebrow>
              <h2 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                A study in <span className="gold-gradient-text">ascent.</span>
              </h2>
            </div>
            <p className="max-w-md text-muted-foreground text-sm leading-relaxed">
              A selection of recent floating stairs, replacements and finishes.
              Every project is one-of-one.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <PortfolioCard span="md:col-span-8" img={PORT_1} title="Curved · Oak" tag="Replacement" year="2025" />
            <PortfolioCard span="md:col-span-4" img={PORT_2} title="Floating · Open" tag="Floating Stairs" year="2025" />
            <PortfolioCard span="md:col-span-5" img={PORT_3} title="Geometry" tag="Custom Build" year="2024" />
            <PortfolioCard span="md:col-span-7" img={PORT_4} title="Sweep" tag="Curved Build" year="2024" />
          </div>

          {/* REELS — autoplaying video portfolio */}
          <div className="mt-24 flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <Eyebrow>Reels in motion</Eyebrow>
              <h3 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                On-site, <span className="gold-gradient-text">in motion.</span>
              </h3>
            </div>
            <p className="max-w-sm text-sm uppercase tracking-[0.22em] text-muted-foreground">
              Built · measured · finished · filmed
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="reels-grid">
            <ReelCard span="md:col-span-7 aspect-[16/9]" src="/clips/clip-1.mp4" poster="/clips/clip-1.jpg" title="Floating walkthrough" tag="Project reel" idx={1} />
            <ReelCard span="md:col-span-5 aspect-[16/9]" src="/clips/clip-2.mp4" poster="/clips/clip-2.jpg" title="Curved build" tag="Build process" idx={2} />
            <ReelCard span="md:col-span-5 aspect-[16/9]" src="/clips/clip-3.mp4" poster="/clips/clip-3.jpg" title="Studio cut" tag="Cinematic" idx={3} />
            <ReelCard span="md:col-span-7 aspect-[16/9]" src="/clips/clip-4.mp4" poster="/clips/clip-4.jpg" title="Install day" tag="On-site" idx={4} />
            <ReelCard span="md:col-span-12 aspect-[21/9]" src="/clips/clip-5.mp4" poster="/clips/clip-5.jpg" title="The reveal" tag="Final walkthrough" idx={5} />
          </div>

          {/* PROJECT SHOWCASE SLIDESHOW */}
          <div className="mt-24 flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <Eyebrow>Project showcase</Eyebrow>
              <h3 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                Posted from the <span className="gold-gradient-text">workshop.</span>
              </h3>
            </div>
            <p className="max-w-sm text-sm uppercase tracking-[0.22em] text-muted-foreground">
              Tap a thumbnail · auto-advances
            </p>
          </div>

          <Showcase />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-32 bg-[#0c0a07] border-t hairline" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow>In their words</Eyebrow>
          <h2 className="font-display mt-6 text-4xl sm:text-5xl leading-[1.05] max-w-3xl">
            Clients speak in <span className="gold-gradient-text">whispers,</span>
            <br />we let the work answer.
          </h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="border hairline bg-card p-8 relative">
                <Quote className="absolute top-6 right-6 h-6 w-6 text-primary/40" strokeWidth={1.2} />
                <p className="font-display text-xl leading-snug text-foreground">"{t.quote}"</p>
                <div className="gold-divider my-6" />
                <p className="text-sm text-foreground">{t.name}</p>
                <p className="text-sm uppercase tracking-[0.22em] text-muted-foreground mt-1">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Eyebrow>Start your project</Eyebrow>
          <h2 className="font-display mt-8 text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
            Let us measure
            <br />
            <span className="gold-gradient-text">your ascent.</span>
          </h2>
          <p className="mt-8 max-w-xl mx-auto text-muted-foreground leading-relaxed">
            Book a quote or a site visit. We'll get back to you within one business day.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link to="/book">
              <Button
                size="lg"
                className="rounded-none bg-primary text-primary-foreground hover:bg-accent px-12 py-7 text-sm uppercase tracking-[0.24em] shadow-[0_0_30px_rgba(212,175,55,0.25)] hover:shadow-[0_0_50px_rgba(212,175,55,0.5)] transition-all"
                data-testid="cta-book-btn"
              >
                Book a quote
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}>
              <Button
                size="lg"
                variant="outline"
                className="rounded-none border-primary/60 text-primary hover:bg-primary/10 px-10 py-7 text-sm uppercase tracking-[0.24em]"
                data-testid="cta-call-btn"
              >
                <Phone className="h-4 w-4 mr-2" />
                {CONTACT.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const PortfolioCard = ({ span, img, title, tag, year }) => (
  <div className={`${span} group overflow-hidden border hairline`}>
    <div className="aspect-[16/10] overflow-hidden">
      <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms]" />
    </div>
    <div className="p-6 flex items-center justify-between bg-card">
      <div>
        <p className="font-display text-2xl">{title}</p>
        <p className="text-sm uppercase tracking-[0.24em] text-muted-foreground mt-1">{tag}</p>
      </div>
      <span className="text-sm uppercase tracking-[0.24em] text-primary">{year}</span>
    </div>
  </div>
);

const ReelCard = ({ span, src, poster, title, tag, idx }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.play().catch(() => {});
          } else {
            e.target.pause();
          }
        });
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      className={`${span} group relative overflow-hidden border hairline bg-card`}
      data-testid={`reel-card-${idx}`}
    >
      <video
        ref={ref}
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.02]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between gap-4">
        <div>
          <p className="font-display text-xl md:text-2xl text-foreground leading-tight">{title}</p>
          <p className="text-sm uppercase tracking-[0.28em] text-muted-foreground mt-1">{tag}</p>
        </div>
        <span className="text-sm uppercase tracking-[0.28em] text-primary border border-primary/40 px-2 py-1">
          Reel {String(idx).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
};

export default Landing;
